import { useCallback, useEffect } from "react"
import { create } from "zustand"

import { auth } from "@/features/auth/providers/firebase"
import { useAuthStore } from "@/stores/auth-store"

import { getMyReports, type UserReport } from "../api/user-reports-service"
import type { ReportId } from "../config/reports-data"

interface UserReportsState {
  reports: UserReport[]
  loading: boolean
  error: Error | null
  /** True once a fetch has completed at least once this session. */
  fetched: boolean
  /** In-flight request, so concurrent consumers share a single network call. */
  inflight: Promise<void> | null
  fetch: (options?: { force?: boolean }) => Promise<void>
  reset: () => void
}

/**
 * Shared cache for `GET /v1/reports/me`. Backing every {@link useUserReports}
 * consumer with one store means the many places that read report state (the
 * reports list rows, the in-progress sheet, the report viewer) trigger a single
 * request, not one each.
 */
const useUserReportsStore = create<UserReportsState>((set, get) => ({
  reports: [],
  loading: false,
  error: null,
  fetched: false,
  inflight: null,
  fetch: ({ force = false } = {}) => {
    const state = get()
    if (state.inflight) return state.inflight
    if (state.fetched && !force) return Promise.resolve()

    const firebaseUser = auth.currentUser
    if (!firebaseUser) {
      // No session to query (e.g. auth bypass) — treat as fetched with an empty list
      // so consumers don't wait forever for ownership data.
      set({ reports: [], fetched: true, loading: false, error: null })
      return Promise.resolve()
    }

    const request = (async () => {
      set({ loading: true, error: null })
      try {
        const token = await firebaseUser.getIdToken()
        set({ reports: await getMyReports(token), fetched: true })
      } catch (err) {
        set({
          error: err instanceof Error ? err : new Error("Failed to load reports"),
          fetched: true,
        })
      } finally {
        set({ loading: false, inflight: null })
      }
    })()

    set({ inflight: request })
    return request
  },
  reset: () => set({ reports: [], loading: false, error: null, fetched: false, inflight: null }),
}))

/**
 * Force-reload the signed-in user's reports (e.g. right after a purchase writes a
 * new report record). Safe to call imperatively outside React.
 */
export function refetchUserReports(): Promise<void> {
  return useUserReportsStore.getState().fetch({ force: true })
}

/**
 * Fetch `/reports/me` without writing the shared cache (liked-review unlock check).
 * Falls back to the in-memory cache if the network/parse yields nothing.
 */
export async function peekUserReports(): Promise<UserReport[]> {
  const cached = useUserReportsStore.getState().reports
  const firebaseUser = auth.currentUser
  if (!firebaseUser) return cached

  try {
    const token = await firebaseUser.getIdToken()
    const fresh = await getMyReports(token)
    if (fresh.length > 0) return fresh
  } catch {
    /* use cache */
  }
  return cached
}

export interface UseUserReports {
  reports: UserReport[]
  byType: (type: ReportId) => UserReport | undefined
  loading: boolean
  /** True after the first fetch attempt finished (success, error, or no session). */
  fetched: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * Reads the signed-in user's reports from `GET /v1/reports/me`. Fetches once per
 * session on mount (only while a Firebase user is signed in) and shares one
 * request across every consumer. `refetch()` forces a reload — used when a
 * report's 10-min unlock window elapses so a now-ready report picks up its url.
 */
export function useUserReports(): UseUserReports {
  const authUser = useAuthStore((s) => s.user)
  const reports = useUserReportsStore((s) => s.reports)
  const loading = useUserReportsStore((s) => s.loading)
  const fetched = useUserReportsStore((s) => s.fetched)
  const error = useUserReportsStore((s) => s.error)
  const fetch = useUserReportsStore((s) => s.fetch)
  const reset = useUserReportsStore((s) => s.reset)

  useEffect(() => {
    if (!authUser) {
      reset()
      return
    }
    void fetch()
  }, [authUser, fetch, reset])

  const byType = useCallback(
    (type: ReportId) => reports.find((report) => report.type === type),
    [reports],
  )

  const refetch = useCallback(() => fetch({ force: true }), [fetch])

  return { reports, byType, loading, fetched, error, refetch }
}
